import { FC, Fragment } from "react";
import { useTranslation } from "react-i18next";

import { IStationTimingPairStatsDto } from "@ryde/shared";

import {
  buildStationTimingLegName,
  getStationTimingDayGroupId,
  IStationTimingPreviewSelection,
  IStationTimingPreviewStation,
  STATION_TIMING_NO_DATA_GROUP_ID,
  STATION_TIMING_PREVIEW_COLUMNS_COUNT,
  StationTimingNoDataToggle,
  StationTimingPreviewDayRow,
  StationTimingPreviewRow,
  StationTimingPreviewTableHead,
  StationTimingPreviewToolbar,
  StationTimingPreviewWrapper,
  useStationTimingPreview,
} from "./parts";

export interface IStationTimingPreviewProps {
  /** Ordered stations of the entity being edited; adjacent pairs form the legs. */
  stations: IStationTimingPreviewStation[];
  /** Planned seconds per leg, index i = station i -> i + 1; `undefined` where the plan is unknown. */
  plannedLegSeconds: (number | undefined)[];
  /** Measured statistics for the same station list, in the same order. */
  stats: IStationTimingPairStatsDto | undefined;
  /** Fires on every selection change with the full leg-seconds array ready to apply. */
  onChange: (selection: IStationTimingPreviewSelection) => void;
  /** Editor-specific caveat shown above the table, e.g. the ML path's whole-minute storage. */
  resolutionNote?: string;
  /**
   * What the figures were narrowed to, for a flow that filters instead of pooling. Without it the ride
   * numbers read as general averages.
   */
  basisNote?: string;
}

export const StationTimingPreview: FC<IStationTimingPreviewProps> = ({
  stations,
  plannedLegSeconds,
  stats,
  onChange,
  resolutionNote,
  basisNote,
}) => {
  const { t } = useTranslation();
  const {
    rowsWithData,
    rowsWithoutData,
    selectionMap,
    selectableLegsCount,
    selectedLegsCount,
    areNoDataLegsExpanded,
    expandedDayLegs,
    handleOptionChange,
    handleSelectAllMedian,
    handleSelectAllAverage,
    handleClearSelection,
    handleNoDataLegsToggle,
    handleDayBreakdownToggle,
  } = useStationTimingPreview({ stations, plannedLegSeconds, stats, onChange });

  return (
    <StationTimingPreviewWrapper>
      {basisNote ? <p className="station-timing__basis">{basisNote}</p> : null}

      <StationTimingPreviewToolbar
        onSelectAllMedian={handleSelectAllMedian}
        onSelectAllAverage={handleSelectAllAverage}
        onClearSelection={handleClearSelection}
      />

      {resolutionNote ? (
        <p className="station-timing__note">{resolutionNote}</p>
      ) : null}

      <p className="station-timing__status" aria-live="polite">
        {t("stationTiming.STATUS", {
          selected: selectedLegsCount,
          selectable: selectableLegsCount,
        })}
      </p>

      <div className="station-timing__scroll">
        <table className="station-timing__table">
          <StationTimingPreviewTableHead />
          {/* One row group per leg: a leg's day rows have to be a single node its expander can control. */}
          {rowsWithData.map(row => (
            <Fragment key={row.legIndex}>
              <tbody>
                <StationTimingPreviewRow
                  row={row}
                  selectedOptionKey={selectionMap[row.legIndex]}
                  isDayBreakdownExpanded={Boolean(
                    expandedDayLegs[row.legIndex],
                  )}
                  onOptionChange={handleOptionChange}
                  onDayBreakdownToggle={handleDayBreakdownToggle}
                />
              </tbody>
              {row.dayRows.length ? (
                // Stays mounted while collapsed so the button's aria-controls always resolves to a node.
                <tbody
                  id={getStationTimingDayGroupId(row.legIndex)}
                  hidden={!expandedDayLegs[row.legIndex]}
                >
                  {row.dayRows.map(dayRow => (
                    <StationTimingPreviewDayRow
                      key={dayRow.dayOfWeek}
                      dayRow={dayRow}
                      plannedSeconds={row.plannedSeconds}
                      legName={buildStationTimingLegName(row)}
                    />
                  ))}
                </tbody>
              ) : null}
            </Fragment>
          ))}
          {rowsWithData.length ? null : (
            <tbody>
              <tr>
                <td
                  className="station-timing__no-data"
                  colSpan={STATION_TIMING_PREVIEW_COLUMNS_COUNT}
                >
                  {t("stationTiming.NO_LEGS_WITH_DATA")}
                </td>
              </tr>
            </tbody>
          )}
          {rowsWithoutData.length ? (
            <>
              <tbody>
                <tr>
                  <td
                    className="station-timing__expander-cell"
                    colSpan={STATION_TIMING_PREVIEW_COLUMNS_COUNT}
                  >
                    <StationTimingNoDataToggle
                      legsCount={rowsWithoutData.length}
                      isExpanded={areNoDataLegsExpanded}
                      onToggle={handleNoDataLegsToggle}
                    />
                  </td>
                </tr>
              </tbody>
              {/* Stays mounted while collapsed so the button's aria-controls always resolves to a node. */}
              <tbody
                id={STATION_TIMING_NO_DATA_GROUP_ID}
                hidden={!areNoDataLegsExpanded}
              >
                {rowsWithoutData.map(row => (
                  <StationTimingPreviewRow
                    key={row.legIndex}
                    row={row}
                    selectedOptionKey={selectionMap[row.legIndex]}
                    isDayBreakdownExpanded={false}
                    onOptionChange={handleOptionChange}
                    onDayBreakdownToggle={handleDayBreakdownToggle}
                  />
                ))}
              </tbody>
            </>
          ) : null}
        </table>
      </div>
    </StationTimingPreviewWrapper>
  );
};
